"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { ArticleArchiveCard } from "@/components/shared/ArticleArchiveCard";
import type { ArticleCard } from "@/components/shared/article-archive-types";

type ArchiveKind = "blog" | "case-studies";

/** Start loading before the sentinel hits the viewport (helps smooth infinite scroll). */
const ROOT_MARGIN = "400px 0px";

function ArchiveLoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="56"
      height="56"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        className="opacity-90"
      />
    </svg>
  );
}

export function ArticleArchiveInfiniteList({
  basePath,
  kind,
  initialPosts,
  initialHasMore,
  searchQuery,
  activeCategorySlug,
}: {
  basePath: string;
  kind: ArchiveKind;
  initialPosts: ArticleCard[];
  initialHasMore: boolean;
  searchQuery: string;
  activeCategorySlug: string | null;
}) {
  const [posts, setPosts] = useState<ArticleCard[]>(initialPosts);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const postsRef = useRef(posts);
  const hasMoreRef = useRef(initialHasMore);
  const loadingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const listKey = `${kind}|${activeCategorySlug ?? ""}|${searchQuery.trim()}`;

  useEffect(() => {
    postsRef.current = posts;
  }, [posts]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    setPosts(initialPosts);
    setHasMore(initialHasMore);
    setError(null);
    setLoading(false);
    loadingRef.current = false;
  }, [listKey, initialPosts, initialHasMore]);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    const offset = postsRef.current.length;
    try {
      const params = new URLSearchParams({
        kind,
        offset: String(offset),
        q: searchQuery.trim(),
      });
      if (activeCategorySlug) params.set("category", activeCategorySlug);

      const res = await fetch(`/api/archive?${params.toString()}`, { method: "GET" });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? `Request failed (${res.status})`);
      }
      const data = (await res.json()) as {
        items: ArticleCard[];
        hasMore: boolean;
      };
      setPosts((prev) => [...prev, ...data.items]);
      setHasMore(data.hasMore);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load more.");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [activeCategorySlug, kind, searchQuery]);

  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        void loadMore();
      },
      { root: null, rootMargin: ROOT_MARGIN, threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, listKey]);

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="mt-12">
      <ul
        className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10"
        aria-busy={loading}
        aria-live="polite"
      >
        {posts.map((post) => (
          <li key={post._id}>
            <ArticleArchiveCard post={post} basePath={basePath} />
          </li>
        ))}
      </ul>

      {error ? (
        <p className="mt-6 text-center text-sm text-red-700" role="alert">
          {error}
        </p>
      ) : null}

      {hasMore ? (
        <div
          ref={sentinelRef}
          className="flex min-h-24 w-full flex-col items-center justify-center py-10"
          aria-hidden={loading ? undefined : true}
        >
          {loading ? (
            <div className="flex flex-col items-center gap-1 text-[#231f20]" role="status">
              <ArchiveLoadingSpinner className="h-14 w-14 shrink-0 animate-spin md:h-16 md:w-16" />
              <span className="sr-only">Loading more</span>
            </div>
          ) : (
            <span className="sr-only">Scroll for more</span>
          )}
        </div>
      ) : null}
    </div>
  );
}
